<template>
  <RouterView v-if="bare" />
  <div v-else class="max-w-full min-h-[100vh] flex flex-col">
    <Header />
    <main class="mb-16 md:mb-24 grow">
      <RouterView />
    </main>
    <Footer />
  </div>
  <!--
    After the page on purpose: the page's setup sets its meta data, so when
    the browser hydrates the head it already matches what the server sent.
    Rendered first, the head would hydrate with the defaults and tags that
    only some pages have (JSON-LD, noindex) would end up duplicated.
  -->
  <teleport to="head">
    <MetaTags :meta-data-fn="metaDataFn" />
    <ConfigState />
  </teleport>
</template>

<script setup lang="ts">
import { MetaTags } from '@/components/MetaTags'
import { useMeta } from '@/composables/Meta'
import { computed, getCurrentInstance, onMounted, onServerPrefetch, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { ConfigState } from '@/components/ConfigState'
import Config from '@/utils/config'
import Footer from '@/components/Footer.vue'
import Header from '@/components/Header.vue'
import { organization } from '@/utils/structuredData'

const { metaDataFn, updateMetaData } = useMeta()
const { t } = useI18n()
const app = getCurrentInstance()
const fullUrl = app?.appContext.config.globalProperties.fullUrl
const userStore = useUserStore()
const route = useRoute()

onServerPrefetch(() => {
  if (!language.value) return
  userStore.setLanguage(language.value)
})

const bare = computed(() => route.matched.some((record) => record.meta.bare === true))

const disableSSR = computed(() => {
  if (!Config.ssr) return false
  return route.matched.some((record) => {
    return record.meta.ssr != undefined && record.meta.ssr === false
  })
})

const language = computed((): string => {
  return (route.params.locale as string) ?? userStore.language ?? Config.languages?.[0]
})

// Share images are exported from the studio cards into public/images/og.
const defaultImage = computed(() => `/images/og/og-default-${userStore.language}.png`)

const resetMetaData = () => {
  return updateMetaData({
    language: computed(() => userStore.language),
    title: computed(() => t('common.seo.title')),
    description: computed(() => t('common.seo.description')),
    image: defaultImage,
    noIndex: computed(() => false),
    url: computed(() => (Config.ssr ? fullUrl : window.location.href)),
    canonical: computed(() => undefined),
    type: computed(() => undefined),
    jsonLd: computed(() => [organization(defaultImage.value)]),
    next: computed(() => undefined),
    prev: computed(() => undefined),
  })
}

resetMetaData()

onMounted(() => {
  document.getElementById('app')?.classList.remove('app-loading')
})

// Reset when another page is shown. A language switch or a different slug
// keeps the same page component, which does not run its setup again, so its
// meta data has to stay in place (it follows the language on its own).
watch(
    () => route.name,
    () => {
      resetMetaData()
    }
)

watch(
    () => language.value,
    (newLanguage) => {
      if (!newLanguage) return
      userStore.setLanguage(newLanguage)
      // The server sets the attribute on the first render; keep it in sync
      // when the visitor switches language in the browser.
      if (!Config.ssr) document.documentElement.lang = newLanguage
    },
    { immediate: true }
)
</script>
